import React, { useState, useEffect } from 'react';
import { showAppAlert } from '../utils/alertService';
import LoaderOverlay from './LoaderOverlay';
import { municipiosRJ } from '../utils/municipios';
import { EMAIL_INVALID_MESSAGE, isValidKnownProviderEmail, normalizeEmail } from '../utils/emailValidation';
import '../styles/formStyles.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;

function maskDate(value) {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\/\d{4})\d+?$/, '$1');
}

function AddClientModal({ isOpen, onClose, onClientAdded }) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [celular, setCelular] = useState('');
  const [dataNascimento, setDataNascimento] = useState('');
  const [genero, setGenero] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [bairro, setBairro] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen) {
      // Pegue o user_id do localStorage (ajuste conforme necessário)
      const userData = localStorage.getItem('user');
      let userId = null;
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          userId = parsedUser.user_id || parsedUser.id || parsedUser.id_usuario;
        } catch {
          userId = null;
        }
      }
      if (userId) {
        fetch(`${API_URL}/companies/by-user-company/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
          .then(res => res.json())
          .then(data => {
            setCompanies(Array.isArray(data) ? data : []);
          })
          .catch(() => setCompanies([]));
      }
    }
  }, [isOpen, token]);

  const handleBlur = () => {
    if (dataNascimento && dataNascimento.length === 10) {
      const parts = dataNascimento.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        const birthDate = new Date(year, month, day);
        const tenYearsAgo = new Date();
        const oneHundredFiftyYearsAgo = new Date();
        tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
        oneHundredFiftyYearsAgo.setFullYear(oneHundredFiftyYearsAgo.getFullYear() - 150);

        if (birthDate > tenYearsAgo) {
          showAppAlert('warning', 'É necessário ter pelo menos 10 anos para se cadastrar.');
        } else if (birthDate < oneHundredFiftyYearsAgo) {
          showAppAlert('warning', 'A data de nascimento não pode ser superior a 150 anos.');
        }
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!isValidKnownProviderEmail(email)) {
      showAppAlert('error', EMAIL_INVALID_MESSAGE);
      setLoading(false);
      return;
    }

    // Converte a data do formato dd/mm/yyyy para yyyy-mm-dd antes de enviar
    let formattedDate = null;
    if (dataNascimento) {
      const parts = dataNascimento.split('/');
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    const newClient = {
      nome,
      email: normalizeEmail(email),
      telefone: (celular || '').replace(/\D/g, ''),
      dataNascimento: formattedDate,
      genero,
      cidade: municipio,
      bairro,
      company_id: companyId,
    };

    try {
      const response = await fetch(`${API_URL}/register-client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newClient),
      });

      if (response.status === 201) {
        showAppAlert('success', 'Cliente adicionado com sucesso!');
        onClientAdded(newClient);
        onClose();
      } else if (response.status === 409) {
        const errorData = await response.json();
        showAppAlert('error', errorData.message || 'Este cliente já está cadastrado para esta empresa.');
      } else if (response.status === 422) {
        const errorData = await response.json();
        // Monta mensagem de erro detalhada
        let msg = errorData.error || 'Erro de validação.';
        if (errorData.details) {
          for (const [field, errors] of Object.entries(errorData.details)) {
            msg += `\n${field}: ${errors.join(', ')}`;
          }
        }
        showAppAlert('error', msg);
      } else if (response.status === 500) {
        const errorData = await response.json();
        let msg = errorData.error || 'Erro ao cadastrar cliente.';
        if (errorData.details) {
          msg += `\n${errorData.details}`;
        }
        showAppAlert('error', msg);
      } else {
        const errorData = await response.json();
        showAppAlert('error', `Erro ao adicionar cliente: ${errorData.message || 'Erro desconhecido.'}`);
      }
    } catch (error) {
      console.error('Erro ao adicionar cliente:', error);
      showAppAlert('error', `Erro ao adicionar cliente: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  function maskCelular(value) {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{2})(\d)/g, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay">
      <LoaderOverlay isLoading={loading} message="Adicionando cliente..." />
      <div className="modal-content">
        <h3 style={{
          borderBottom: '2px solid #C8A2C8',
          paddingBottom: '5px',
          fontSize: 'clamp(1.2rem, 2vw, 1.5rem)'
        }}>Adicionar Cliente</h3>
        <form onSubmit={handleSave}>
          <div className="label-style">
            <label>Nome:</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="input-style input-medium"
              required
            />
          </div>
          
          <div className="label-style">
            <label>Email:</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-style input-medium"
              required
            />
          </div>
          
          <div className="form-row">
            <div className="label-style flex-1">
              <label>Celular:</label>
              <input
                type="text"
                value={celular}
                onChange={e => setCelular(maskCelular(e.target.value))}
                className="input-style"
                maxLength={15}
              />
            </div>
          </div>
          
          <div className="label-style">
            <label>Data de Nascimento:</label>
            <input
              type="text"
              value={dataNascimento}
              onChange={e => setDataNascimento(maskDate(e.target.value))}
              onBlur={handleBlur}
              className="input-style"
              placeholderText="dd/mm/aaaa"
              maxLength="10"
            />
          </div>
          
          <div className="form-row">
            <div className="label-style flex-1">
              <label>Gênero:</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="genero"
                    value="masculino"
                    checked={genero === 'masculino'}
                    onChange={(e) => setGenero(e.target.value)}
                  />
                  Masculino
                </label>
                <label>
                  <input
                    type="radio"
                    name="genero"
                    value="feminino"
                    checked={genero === 'feminino'}
                    onChange={(e) => setGenero(e.target.value)}
                  />
                  Feminino
                </label>
                <label>
                  <input
                    type="radio"
                    name="genero"
                    value="outro"
                    checked={genero === 'outro'}
                    onChange={(e) => setGenero(e.target.value)}
                  />
                  Outro
                </label>
              </div>
            </div>
            <div className="label-style flex-1">
              <label>Empresa:</label>
              <select
                value={companyId}
                onChange={e => setCompanyId(e.target.value)}
                className="input-style"
                required
              >
                <option value="">Selecione a empresa...</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.nomeFantasia}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="label-style flex-1">
              <label>Município:</label>
              <select
                value={municipio}
                onChange={(e) => setMunicipio(e.target.value)}
                className="input-style"
              >
                <option value="">Selecione um município...</option>
                {municipiosRJ.map((mun, index) => (
                  <option key={index} value={mun}>
                    {mun}
                  </option>
                ))}
              </select>
            </div>
            <div className="label-style flex-1">
              <label>Bairro:</label>
              <input
                type="text"
                value={bairro}
                onChange={(e) => setBairro(e.target.value)}
                className="input-style input-small"
              />
            </div>
          </div>
          
          <div className="button-container">
            <button type="submit" className="button-style" disabled={loading}>
              {loading ? 'Adicionando...' : 'Adicionar'}
            </button>
            <button type="button" onClick={onClose} className="button-style cancel" disabled={loading}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddClientModal;
